import {
  useIsLoggedIn,
  useSessionTyped,
  useUserIdWithLoading,
} from '@dreamstack/auth'
import { useLocale, useTranslation } from '@dreamstack/i18n'
import { useTrackFormSubmissionGA4 } from '@dreamstack/tracking'
import { getOrCreateAnonymousId } from '@dreamstack/tracking/src/getOrCreateAnonymousId'
import { trpc } from '@dreamstack/trpc/client'
import { useRouter } from 'next/router'
import type { FunctionComponent } from 'react'
import { useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { AccessFileForm } from '../accessFile/AccessFileForm'
import { getAccessFileUrls } from '../accessFile/getAccessFileUrls'
import type { AccessFileFormData } from '../accessFile/useAccessFileFormSchema'
import { useAccessFileFormSchema } from '../accessFile/useAccessFileFormSchema'
import { useSuccessDialog } from '../confirmation/useSuccessDialog'
import { NewsletterType } from '../newsletter/NewsletterTypes.static'

const baseUrl = process.env.NEXT_PUBLIC_NEXT_BASE_URL

export const CapitalInvestmentOrderGuideForm: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const yupSchemaResolver = useAccessFileFormSchema()
  const methods = useForm({ resolver: yupSchemaResolver })
  const t = useTranslation()
  const router = useRouter()
  const locale = useLocale()
  const { userId } = useUserIdWithLoading()

  const trackFormSubmissionForGA4 = useTrackFormSubmissionGA4()

  const { mutateAsync: trackFormSubmissionServer } =
    trpc.anonymousTracking.trackEvent.useMutation()
  const isLoggedIn = useIsLoggedIn()
  const session = useSessionTyped()

  const {
    mutateAsync: sendCapitalInvestmentGuide,
    isPending: laodingCapitalInvestmentGuide,
  } = trpc.newsletter.sendCapitalInvestmentGuide.useMutation()

  const {
    mutateAsync: sendNewsletterRegistration,
    isPending: loadingNewsletter,
  } = trpc.newsletter.sendRegisterNewsletter.useMutation()

  const somethingIsLoading = useMemo(() => {
    return laodingCapitalInvestmentGuide || loadingNewsletter
  }, [laodingCapitalInvestmentGuide, loadingNewsletter])

  const redirectUrl = getAccessFileUrls(locale, 'capitalGuide', 'redirect')

  const returnToLandingpage = () => {
    router.push(redirectUrl)
  }

  const { SuccessDialog, show } = useSuccessDialog({
    title: t('accentro:capitalInvestmentGuideOrderForm.modalTitle'),
    text: t('accentro:capitalInvestmentGuideOrderForm.modalBody'),
    ctaLabel: t('accentro:capitalInvestmentGuideOrderForm.modalCta'),
    onCtaClick: returnToLandingpage,
  })
  const { handleSubmit, control, reset } = methods

  // TODO: Refactor to one place. See other comments.
  const { data: jwtHrefData } = trpc.user.getJwtForOptInAccess.useQuery(
    { type: 'CapitalInvestmentGuide' },
    {
      enabled: isLoggedIn,
      trpc: { ssr: false },
      staleTime: 0,
    }
  )

  // TODO: Refactor to one place. See other comments.
  const onSubmit = useCallback(
    async (data: AccessFileFormData) => {
      const anonymousUserId = getOrCreateAnonymousId()
      const actions = []
      const trackingData = {
        formId: 'capitalGuide',
        formData: {
          ...data,
          locale,
        },
      }
      if (!isLoggedIn)
        actions.push(
          sendCapitalInvestmentGuide({
            email: data.clientEmail,
            locale,
          }),
          trackFormSubmissionServer({
            eventName: 'formSubmission',
            userId,
            anonymousUserId,
            data: trackingData,
          })
        )

      if (data.subscribeNewsletter) {
        const email = isLoggedIn ? session?.email : data.clientEmail
        if (email) {
          actions.push(
            sendNewsletterRegistration({
              email,
              type: NewsletterType.Newsletter,
              locale,
              wantsNewsletter: true,
            }),
            trackFormSubmissionServer({
              eventName: 'formSubmission',
              userId,
              anonymousUserId,
              data: {
                formId: 'Registered for Newsletter',
                formData: {
                  email,
                },
              },
            })
          )
        }
      }
      await Promise.all(actions)

      trackFormSubmissionForGA4(trackingData)

      !isLoggedIn && show()
      if (isLoggedIn) {
        window.location.href = `${baseUrl}/${jwtHrefData?.href}`
      }
      try {
        reset()
      } catch (error) {}
    },
    [
      locale,
      isLoggedIn,
      sendCapitalInvestmentGuide,
      trackFormSubmissionServer,
      userId,
      trackFormSubmissionForGA4,
      show,
      session?.email,
      sendNewsletterRegistration,
      jwtHrefData?.href,
      reset,
    ]
  )

  return (
    <>
      {SuccessDialog}
      <AccessFileForm
        methods={methods}
        handleSubmit={handleSubmit}
        control={control}
        onSubmit={onSubmit}
        loading={somethingIsLoading}
      />
    </>
  )
}
